import React, { useState } from 'react';
import styles from './PaymentInvoices.module.css';
import CompanySearchSelect from './CompanySearchSelect';

// ─── UTC date helpers (ADR 009) ───────────────────────────────────────────────
// Calendar dates are stored as UTC day boundaries ("YYYY-MM-DDT00:00:00.000Z").
// See adr/009-handling-date-filters.md for the full rationale.

const dateInputToUtcIso = value => {
  if (!value) return '';
  const [y, m, d] = value.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toISOString();
};

const toInputDate = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, '0'),
    String(d.getUTCDate()).padStart(2, '0'),
  ].join('-');
};

// UTC-safe day offset; n may be negative.
const utcOffsetDays = (dateOrIso, n) => {
  const d = new Date(dateOrIso);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n),
  ).toISOString();
};

// Snaps to the nearest Tuesday on or before the given date.
const snapToTuesdayOnOrBefore = dateOrIso => {
  const d = new Date(dateOrIso);
  const daysBack = (d.getUTCDay() - 2 + 7) % 7;
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - daysBack),
  ).toISOString();
};

// Returns { from, to } for the last completed Tue–Mon billing cycle.
// to is the exclusive upper bound (next Tuesday 00:00 UTC).
const lastCompletedBillingWeek = () => {
  const now = new Date();
  const daysSinceTue = (now.getUTCDay() - 2 + 7) % 7;
  const thisTue = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - daysSinceTue,
    ),
  );
  const from = new Date(
    Date.UTC(
      thisTue.getUTCFullYear(),
      thisTue.getUTCMonth(),
      thisTue.getUTCDate() - 7,
    ),
  ).toISOString();
  return { from, to: thisTue.toISOString() };
};

// Returns the next Friday as a UTC ISO string (today if today is Friday UTC).
// getUTCDay() + Date.UTC so the result is unaffected by the browser's timezone.
const getNextFriday = () => {
  const now = new Date();
  const daysUntilFriday = (5 - now.getUTCDay() + 7) % 7;
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + daysUntilFriday,
    ),
  ).toISOString();
};

const GenerateInvoiceModal = ({
  isOpen,
  onClose,
  onPreview: _onPreview,
  onGenerate,
  generateError,
  isLoading,
}) => {
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [fromDate, setFromDate] = useState(
    () => lastCompletedBillingWeek().from,
  );
  const [toDate, setToDate] = useState(() => lastCompletedBillingWeek().to);
  const [paymentDate, setPaymentDate] = useState(getNextFriday());
  const [autoGeneratePdf, setAutoGeneratePdf] = useState(true);
  const [autoSendEmail, setAutoSendEmail] = useState(true);
  const [requireApproval, setRequireApproval] = useState(false);
  const [isCashInclude, setIsCashInclude] = useState(false);
  const [isKraveinFeeInclude, setIsKraveinFeeInclude] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const allCompanies = selectedCompanies.some(c => c.companyId === 'ALL');

  const errors = {
    companies:
      !allCompanies && selectedCompanies.length === 0
        ? 'At least one company is required'
        : null,
    fromDate: !fromDate ? 'From date is required' : null,
    toDate: !toDate ? 'To date is required' : null,
    paymentDate: !paymentDate ? 'Payment date is required' : null,
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const canSubmit = !hasErrors;

  const handleGenerate = () => {
    setSubmitted(true);
    if (hasErrors) return;
    onGenerate({
      companies: selectedCompanies,
      fromDate,
      toDate,
      paymentDate,
      autoGeneratePdf,
      autoSendEmail,
      requireApproval,
      isKraveinFeeInclude,
      isCashInclude,
      allCompanies,
    });
  };

  const handleToggleCompany = company => {
    setSelectedCompanies(prev => {
      const next = prev.some(c => c.companyId === company.companyId)
        ? prev.filter(c => c.companyId !== company.companyId)
        : [...prev, company];
      setAutoSendEmail(
        next.length === 1 ? next[0].autoSendEmail ?? true : true,
      );
      return next;
    });
  };

  return (
    <div
      className={styles.modalBg}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modalBox} style={{ width: 540 }}>
        <div className={styles.modalTitle}>🧾 Generate Invoices</div>
        <div className={styles.modalSub}>
          Configure the invoice generation run
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className={styles.formLabel}>Company *</div>
          <CompanySearchSelect
            multiSelect
            selectedCompanies={selectedCompanies}
            onToggle={handleToggleCompany}
            placeholder="Search by company name..."
          />
          {submitted && errors.companies && (
            <div style={{ fontSize: 11, color: 'var(--rd)', marginTop: 4 }}>
              {errors.companies}
            </div>
          )}
        </div>

        <div className={styles.modalTwoCol}>
          <div>
            <div className={styles.formLabel}>Date From *</div>
            <input
              type="date"
              className={styles.formInput}
              value={toInputDate(fromDate)}
              min="1970-01-06"
              step="7"
              onChange={e => {
                if (!e.target.value) return;
                const tue = snapToTuesdayOnOrBefore(
                  dateInputToUtcIso(e.target.value),
                );
                setFromDate(tue);
                setToDate(utcOffsetDays(tue, 7));
              }}
            />
            {submitted && errors.fromDate && (
              <div style={{ fontSize: 11, color: 'var(--rd)', marginTop: 4 }}>
                {errors.fromDate}
              </div>
            )}
          </div>
          <div>
            <div className={styles.formLabel}>Date To</div>
            <input
              type="date"
              className={styles.formInput}
              value={toDate ? toInputDate(utcOffsetDays(toDate, -1)) : ''}
              disabled
            />
            {submitted && errors.toDate && (
              <div style={{ fontSize: 11, color: 'var(--rd)', marginTop: 4 }}>
                {errors.toDate}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className={styles.formLabel}>Payment Date * </div>
          <input
            type="date"
            className={styles.formInput}
            value={toInputDate(paymentDate)}
            min={toInputDate(new Date().toISOString())}
            // step="7"
            onChange={e => setPaymentDate(dateInputToUtcIso(e.target.value))}
          />
          {submitted && errors.paymentDate && (
            <div style={{ fontSize: 11, color: 'var(--rd)', marginTop: 4 }}>
              {errors.paymentDate}
            </div>
          )}
        </div>

        {selectedCompanies.length > 0 && (
          <div className={styles.optionBox}>
            <div className={styles.optionBoxTitle}>Invoice Options</div>
            <div className={styles.optionList}>
              {[
                // [
                //   autoGeneratePdf,
                //   setAutoGeneratePdf,
                //   'Auto-generate PDF + CSV → S3 bucket',
                // ],
                [
                  autoSendEmail,
                  setAutoSendEmail,
                  'Auto-send email to venues on generation',
                ],
                [
                  isCashInclude,
                  setIsCashInclude,
                  'Include cash orders in invoice',
                ],
                // [
                //   isKraveinFeeInclude,
                //   setIsKraveinFeeInclude,
                //   'Include Kravein fee in invoice',
                // ],
                // [
                //   requireApproval,
                //   setRequireApproval,
                //   'Require approval before sending (overrides company setting)',
                // ],
                // [
                //   showKraveinCharges,
                //   setShowKraveinCharges,
                //   'Show KRAVEiN Charges column (override all venues)',
                // ],
              ].map(([val, setter, label]) => (
                <label key={label} className={styles.optionItem}>
                  <input
                    type="checkbox"
                    checked={val}
                    onChange={e => setter(e.target.checked)}
                    style={{ accentColor: 'var(--or)' }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className={styles.infoBox}>
          ℹ Invoice format: <strong>KRV-[PlaceCode]-YYYYMMM-001</strong> · PDF
          emailed as attachment · PDF + CSV stored in S3{' '}
          <code className={styles.infoCode}>kravein-invoices/YYYY/MMM/</code>
        </div>

        {generateError && (
          <div
            style={{
              padding: '10px 14px',
              background: '#fff0f0',
              border: '1px solid #f5c2c2',
              borderRadius: 8,
              marginBottom: 14,
              fontSize: 12.5,
              color: '#c0392b',
            }}
          >
            {generateError.message ||
              'An unexpected error occurred. Please try again.'}
          </div>
        )}

        <div className={styles.modalActions}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            style={{ flex: 1, justifyContent: 'center', padding: 11 }}
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className={`${styles.btn} ${styles.btnPrimary} ${
              !canSubmit || isLoading ? styles.btnDisabled : ''
            }`}
            style={{ flex: 2, justifyContent: 'center', padding: 11 }}
            onClick={handleGenerate}
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Generating...' : '🚀 Generate & Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoiceModal;
